import clsx from 'clsx'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { useKeypressListener } from '../../hooks/useKeypressListener'
import {
    currencyFormat,
    currencyFormatNoRounding,
    shiftCurrencyLeft,
    shiftCurrencyRight,
} from '../../utils/numbers'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { Clickable } from '../common/Clickable'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextArea } from '../common/fields/TextArea'

function BackspaceIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M17 9L11 15M11 9L17 15M2.72 12.96L7.04 18.72C7.392 19.1893 7.568 19.424 7.79105 19.5932C7.9886 19.7432 8.21232 19.855 8.45077 19.9231C8.72 20 9.01334 20 9.6 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H9.6C9.01334 4 8.72 4 8.45077 4.07689C8.21232 4.14499 7.9886 4.25685 7.79105 4.40675C7.568 4.576 7.392 4.81067 7.04 5.28L2.72 11.04C2.46181 11.3843 2.33271 11.5564 2.28294 11.7454C2.23902 11.9123 2.23902 12.0877 2.28294 12.2546C2.33271 12.4436 2.46181 12.6157 2.72 12.96Z"
                stroke="#0F0F0F"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

type KeypadProps = {
    onSubmit: (value: number, description?: string) => void
    min: number
    max: number
    showPaymentLimits: boolean
}

function KeypadButton({
    value,
    onClick,
    className,
    headingSize = 'lg',
    disabled = false,
}: {
    value: number | JSX.Element
    onClick: () => void
    className?: string
    headingSize?: 'lg' | 'md' | 'sm' | 'xs'
    disabled?: boolean
}) {
    return (
        <Clickable
            className={twMerge(
                'flex flex-col justify-center outline-1 outline-gray-300',
                disabled && 'cursor-not-allowed',
                className
            )}
            disabled={disabled}
            onClick={() => {
                if (!disabled) {
                    onClick()
                }
                setTimeout(() => {
                    document.body.focus()
                }, 10)
            }}
        >
            <Heading
                size={headingSize}
                className="flex w-full items-center justify-center text-center"
            >
                {value}
            </Heading>
        </Clickable>
    )
}

const TotalDisplay = forwardRef<
    HTMLInputElement,
    {
        value?: number
        maxReached: boolean
        min: number
        max: number
        onChange: (value: number) => void
        showPaymentLimits: boolean
    }
>(({ value, maxReached, min, max, onChange, showPaymentLimits }, ref) => {
    return (
        <div className="mt-8">
            <CurrencyField
                value={value ? `$${value}` : '$0.00'}
                icon={null}
                onChange={(e) => {
                    onChange(e ?? 0)
                }}
                formatCurrency
                allowEnterToPropagate
                size="medium"
                hasError={maxReached}
                className={clsx('text-heading-lg relative h-[72px] text-center font-bold', {
                    'border-primary-700 outline-primary-700': Boolean(value) && !maxReached,
                    'outline-danger-400 border-danger-400': maxReached,
                })}
                onClear={() => onChange(0)}
                ref={ref}
            />
            {maxReached && !showPaymentLimits && (
                <Paragraph size="sm" className={clsx('text-danger-400 mt-3 text-center')}>
                    {`Cannot exceed ${currencyFormat(max)}`}
                </Paragraph>
            )}
            {showPaymentLimits && (
                <Paragraph
                    size="sm"
                    className={clsx(
                        'mt-3 text-center',
                        {
                            'text-danger-400': maxReached,
                        },
                        {
                            'text-gray-600': !maxReached,
                        }
                    )}
                >
                    Enter a value between{' '}
                    <Button
                        intent="tertiaryLink"
                        className="text-paragraph-sm text-primary-700 p-0"
                        onClick={() => onChange(min)}
                    >
                        {currencyFormat(min)}
                    </Button>{' '}
                    and{' '}
                    <Button
                        intent="tertiaryLink"
                        className="text-paragraph-sm text-primary-700 p-0"
                        onClick={() => onChange(max)}
                    >
                        {currencyFormat(max)}
                    </Button>{' '}
                </Paragraph>
            )}
        </div>
    )
})

export function NewChargeKeypad({ onSubmit, min, max, showPaymentLimits }: KeypadProps) {
    const [keypadValue, setKeypadValue] = useState<number | undefined>(
        showPaymentLimits ? max : undefined
    )
    const [showMemoTextArea, setShowMemoTextArea] = useState(false)
    const [description, setDescription] = useState<string | undefined>(undefined)
    const [isMaxExceeded, setIsMaxExceeded] = useState(false)

    const keypadRef = useRef<HTMLDivElement>(null)
    const totalDisplayRef = useRef<HTMLInputElement>(null)
    const descriptionRef = useRef<HTMLTextAreaElement>(null)

    useKeypressListener(
        ['Enter', 'c', 'C'],
        (key) => {
            if (key === 'C' || key === 'c') {
                setKeypadValue(0)
                setDescription(undefined)
                setShowMemoTextArea(false)
                totalDisplayRef.current?.focus()
            }
            else {
                handleSubmit()
            }
        },
        [document.body, keypadRef]
    )
    const keypadPressed = useCallback(
        (keypadValue: number) => {
            setKeypadValue((v) => {
                const newValue = shiftCurrencyLeft(v, keypadValue)
                if (newValue < 0) {
                    return 0
                }
                if (newValue > max) {
                    setIsMaxExceeded(true)
                }
                return newValue
            })
        },
        [min, max]
    )

    const handleSubmit = useCallback(() => {
        if (isMaxExceeded || !keypadValue) return

        onSubmit(keypadValue, description)
        setKeypadValue(0)
        setDescription(undefined)
        setShowMemoTextArea(false)
        // totalDisplayRef.current?.focus()
    }, [keypadValue, description, onSubmit])

    const isDisabled = (keypadValue ?? 0) < min || isMaxExceeded || keypadValue === 0

    return (
        <div ref={keypadRef}>
            {/* value display section */}
            <TotalDisplay
                ref={totalDisplayRef}
                value={currencyFormatNoRounding(keypadValue).slice(1)}
                maxReached={isMaxExceeded}
                min={min}
                max={max}
                onChange={(newValue) => {
                    if (newValue > max) {
                        setIsMaxExceeded(true)
                    }
                    else {
                        setIsMaxExceeded(false)
                    }
                    setKeypadValue(Number.isNaN(newValue) ? 0 : newValue)

                    // totalDisplayRef.current?.focus()
                }}
                showPaymentLimits={showPaymentLimits}
            />

            {/* memo section */}
            {showMemoTextArea ? (
                <TextArea
                    ref={descriptionRef}
                    isAutoResizing
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter item description"
                    className="mt-4 mb-2 h-fit w-full"
                    onClear={() => setDescription('')}
                    clearButtonClassName="top-6"
                />
            ) : (
                <Button
                    intent="tertiary_deprecated"
                    onClick={() => {
                        setShowMemoTextArea(true)
                        // timeout to ensure the textarea is mounted
                        setTimeout(() => {
                            descriptionRef.current?.focus()
                        }, 0)
                    }}
                    className="mx-auto my-2.5 flex w-fit justify-center"
                    size="small"
                >
                    Add description
                </Button>
            )}

            {/* keypad section */}
            <div className="grid min-h-[236px] grid-cols-3 gap-[1px] rounded-lg text-center">
                <KeypadButton
                    value={1}
                    className="rounded-tl-lg"
                    onClick={() => {
                        keypadPressed(1)
                    }}
                />
                <KeypadButton
                    value={2}
                    onClick={() => {
                        keypadPressed(2)
                    }}
                />
                <KeypadButton
                    value={3}
                    className="rounded-tr-lg"
                    onClick={() => {
                        keypadPressed(3)
                    }}
                />
                {[4, 5, 6, 7, 8, 9].map((k) => (
                    <KeypadButton
                        key={k}
                        value={k}
                        onClick={() => {
                            keypadPressed(k)
                        }}
                    />
                ))}
                <KeypadButton
                    value={<BackspaceIcon />}
                    className="rounded-bl-lg"
                    onClick={() => {
                        setKeypadValue(shiftCurrencyRight(keypadValue))
                    }}
                />
                <KeypadButton
                    value={0}
                    onClick={() => {
                        if (keypadValue !== 0) {
                            keypadPressed(0)
                        }
                    }}
                />

                <Clickable
                    className={`group rounded-br-lg text-white outline-1 outline-gray-300 ${isDisabled && 'cursor-not-allowed'}`}
                    disabled={isDisabled}
                    onClick={handleSubmit}
                >
                    <span
                        className={clsx(
                            'inline-flex items-center justify-center font-bold transition-colors',
                            'focus-visible:ring-info-400 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
                            'h-9 rounded-lg px-3 text-sm',
                            '',
                            { 'cursor-not-allowed bg-gray-200 text-gray-400': isDisabled },
                            {
                                'bg-primary-700 group-hover:bg-primary-700 text-white group-hover:bg-linear-to-b group-hover:from-white/10 group-hover:to-white/10 group-hover:text-white':
                                    !isDisabled,
                            },
                            {
                                'active:bg-primary-700 active:bg-linear-to-b active:from-white/20 active:to-white/20':
                                    !isDisabled,
                            }
                        )}
                    >
                        Add
                    </span>
                </Clickable>
            </div>
        </div>
    )
}
